import React, { useEffect, useRef, useState } from 'react'
import * as Sentry from '@sentry/react'
import { useAtom } from 'jotai'
import { useKey } from 'react-use'
import { useNavigate } from 'react-router-dom'
import { BASE_URL, ENABLE_LOGIN_DEVTOOLS } from '../../utilities/constants'

import {
  reservationConfirmation,
  reservationFolio,
  pinpadPayment,
  reservationTickets,
  puntoVentaID,
  estacionID,
  accessToken,
  onAdmin,
  bookingSessionOrigin,
  bookingSessionDestination,
  bookingSessionDepartureDate,
  bookingSessionReturningDate,
  bookingSessionDepartureTrip,
  bookingSessionDepartureDisponibilidad,
  bookingSessionDepartureSelectedTickets,
  bookingSessionDepartureBlockedTickets,
  bookingSessionTickets,
  bookingSessionPayments,
  bookingSessionReturningBlockedTickets,
  bookingSessionReturningTrip,
  bookingSessionDepartureTripId,
  bookingSessionReturningTripId,
  bookingSessionReset,
  sessionCanReserve,
  bookingSessionCountFares,
  bookingSessionTrips,
  bookingSessionFares,
  bookingSessionCart,
  bookingSessionPassengers,
  bookingSessionGlobalTrips,
  TranspaisIDToken,
  Credentials,
  claveUsuario,
  usuarioID,
  nombrePuntoVenta,
  settingsConfig,
  bookingSessionTimeoutDeadline
} from '../../utilities/atoms'

import { usePinpad } from '../../contexts/pinpad.context'
import { Loader, Departure, Timer } from '../../components/transpais'
import { useModal } from '../../contexts/modal.context'
import { useLogger } from '../../contexts/logger.context'

import { BusBrand } from '../../components/transpais/enums'

import gateway from '../../services/gateway'
import transpais from '../../services/transpais'
import '../../scss/Layout.scss'
import styles from './Dashboard.module.scss'

import Footer from './components/Footer'
import Module from './components/Module'
import { DevToolsToggle } from './Login.styled'
import { useTimer } from '../../contexts/timer.context'
import { decodeToken } from '../../utilities/token'
import jwt_decode from "jwt-decode"
import { startBookingTimer, clearBookingTimer } from '../../helpers/bookingTimer'
import { ensureElectronPinpadReady } from '../../helpers/electronPinpadCredentials'
import { handleApiError, showErrorAlert } from '../../helpers/errorHandler'
import { getPinpadFlowId, logFlowStep, startPinpadFlow } from '../../utilities/pinpad-v2/flow-logger'

const Dashboard = () => {

  const navigate = useNavigate()
  Sentry.configureScope(scope => scope.clear())
  const [canToggleDevTools, setCanToggleDevTools] = useState(false)

  useEffect(() => {
    setCanToggleDevTools(
      ENABLE_LOGIN_DEVTOOLS &&
      typeof window !== 'undefined' &&
      typeof window.electronAPI?.toggleDevTools === 'function'
    )
  }, [])

  const toggleDevTools = async () => {
    try {
      await window.electronAPI.toggleDevTools()
    } catch (error) {
      console.warn('[Dashboard] No se pudo abrir DevTools', error)
    }
  }

  // Todos los atoms para reset
  const [isOnAdmin, setIsOnAdmin] = useAtom(onAdmin)

  useEffect(() => {
    const message = {
      type: 'KIOSKO_TO_ELECTRON',
      payload: { action: 'TEST', message: 'Hola desde React' },
    }
    console.log('[KIOSKO][postMessage][BEFORE]', message)
    window.postMessage(message, '*')
    console.log('[KIOSKO][postMessage][SENT]', message)
  }, [])

  useEffect(() => {
    const url = process.env.REACT_APP_PINPAD_BRIDGE_HTTP_URL || 'http://localhost:45099'
    const payload = { Action: 'STATUS' }

    let cancelled = false

    const run = async () => {
      console.log('[PINPAD][BRIDGE_HTTP][STATUS][BEFORE]', { url, payload })

      const controller = new AbortController()
      const timeout = setTimeout(() => controller.abort(), 15000)

      try {
        const res = await fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
          signal: controller.signal,
        })

        const text = await res.text()
        if (cancelled) return

        console.log('[PINPAD][BRIDGE_HTTP][STATUS][AFTER]', {
          url,
          httpStatus: res.status,
          body: text,
        })
      } catch (error) {
        if (cancelled) return
        console.error('[PINPAD][BRIDGE_HTTP][STATUS][ERROR]', {
          url,
          message: error?.message || String(error),
        })
      } finally {
        clearTimeout(timeout)
      }
    }

    run()

    return () => {
      cancelled = true
    }
  }, [])

  const [token, setToken] = useAtom(accessToken)
  const [punto_venta_id,] = useAtom(puntoVentaID)
  const [estacion_id,] = useAtom(estacionID)

  const [, setFolio] = useAtom(reservationFolio)
  const [, setTickets] = useAtom(reservationTickets)
  const [, setPayment] = useAtom(pinpadPayment)
  const [, setConfirmation] = useAtom(reservationConfirmation)

  const [, setBookingReset] = useAtom(bookingSessionReset)
  const [, setBookingOrigin] = useAtom(bookingSessionOrigin)
  const [, setBookingDestination] = useAtom(bookingSessionDestination)
  const [, setBookingDepartureDate] = useAtom(bookingSessionDepartureDate)
  const [, setBookingReturningDate] = useAtom(bookingSessionReturningDate)
  const [, setBookingDepartureTrip] = useAtom(bookingSessionDepartureTrip)
  const [, setBookingDepartureDisponibilidad] = useAtom(bookingSessionDepartureDisponibilidad)
  const [, setBookingDepartureSelectedTickets] = useAtom(bookingSessionDepartureSelectedTickets)
  const [, setBookingDepartureBlockedTickets] = useAtom(bookingSessionDepartureBlockedTickets)
  const [, setBookingDepartureTripId] = useAtom(bookingSessionDepartureTripId)
  const [, setBookingReturningTrip] = useAtom(bookingSessionReturningTrip)
  const [, setBookingReturningTripId] = useAtom(bookingSessionReturningTripId)
  const [, setBookingReturningBlockedTickets] = useAtom(bookingSessionReturningBlockedTickets)
  const [, setBookingTickets] = useAtom(bookingSessionTickets)
  const [, setBookingPayment] = useAtom(bookingSessionPayments)
  const [, setSessionCanReserve] = useAtom(sessionCanReserve)
  const [, setCount] = useAtom(bookingSessionCountFares)
  const [, setBookingSession] = useAtom(bookingSessionTrips)
  const [, setFares] = useAtom(bookingSessionFares)
  const [, setBookingCartData] = useAtom(bookingSessionCart)
  const [, setPassengers] = useAtom(bookingSessionPassengers)
  const [, setBookingGlobalTrips] = useAtom(bookingSessionGlobalTrips)
  const [transpaisIDToken, setTranspaisIDToken] = useAtom(TranspaisIDToken)
  const [credentials, setCredentials] = useAtom(Credentials)
  // eslint-disable-next-line no-unused-vars
  const [settings, setSettings] = useAtom(settingsConfig)
  const [, setTimeoutDeadline] = useAtom(bookingSessionTimeoutDeadline)

  const barcodeInputValue = useRef('')
  const [localLoading, setLocalLoading] = useState(false)
  const {showModal, hideModal, setupModal} = useModal()
  const {hideTimer} = useTimer()

  const {/* loading */ reset, changeCompany, charge} = usePinpad() // loading no usado aquí
  const {loggerDebug, loggerError} = useLogger()
 
  // eslint-disable-next-line no-unused-vars
  const [cveUsuario, setCveUsuario] = useAtom(claveUsuario)
  // eslint-disable-next-line no-unused-vars
  const [usuarioId, setUsuarioId] = useAtom(usuarioID)
  // eslint-disable-next-line no-unused-vars
  const [puntoVentaId, setPuntoVentaId] = useAtom(puntoVentaID)
  // eslint-disable-next-line no-unused-vars
  const [estacionId, setEstacionId] = useAtom(estacionID)
  // const [token, setToken] = useAtom(accessToken)
  // const [transpaisIDToken, setTranspaisIDToken] = useAtom(TranspaisIDToken)
  // eslint-disable-next-line no-unused-vars
  const [nombPuntoVenta, setNombPuntoVenta] = useAtom(nombrePuntoVenta)

  const logout = () => {
    setTranspaisIDToken('')
    setCveUsuario('')
    setUsuarioId(0)
    setPuntoVentaId(0)
    setEstacionId(0)
    setToken('')
    setNombPuntoVenta('')
  }
  
  useEffect(() => {
    const validateCredentials = async () => {
      if (!credentials || Object.keys(credentials).length === 0) {
        console.warn('Credentials está vacío. Validando con el servicio transpais.credentials...');
  
        try {
          const response = await transpais.credentials(transpaisIDToken); // Llamar al servicio credentials
          console.log('Respuesta de credentials:', response);
  
          // Actualizar el átomo Credentials con la información obtenida
          setCredentials({
            name: response.data.name,
            value: response.data.value,
            pin: response.data.pin, // Asegúrate de que el servicio devuelva `pin`
          });
  
          console.log('Credentials actualizado correctamente.');
        } catch (error) {
          console.error('Error al validar credentials:', error)
          validateCredentials();
          // const { userMessage } = handleApiError(error, { action: 'validateCredentials' })
          // showErrorAlert(userMessage, { setupModal, showModal, hideModal })
        }
      }
    };
  
    validateCredentials();
    
    //Reset contexts
    Sentry.configureScope(scope => scope.clear())
    // Reset reservation
    setFolio('')
    setTickets([])
    setPayment({})
    setConfirmation({})
    reset()
    // Reset booking
    setBookingDepartureTripId(null)
    setBookingReturningTripId(null)
    setBookingOrigin({})
    setBookingDestination({})
    setBookingDepartureDate('')
    setBookingReturningDate('')

    setBookingDepartureTrip(null)
    setBookingDepartureDisponibilidad({})
    setBookingDepartureSelectedTickets([])
    setBookingDepartureBlockedTickets([])

    setBookingReturningTrip(null)
    setPassengers([])
    setBookingGlobalTrips([])
    setBookingReturningBlockedTickets([])
    setSessionCanReserve({})

    setBookingTickets([])
    setBookingPayment([])

    setBookingReset(false)
    clearBookingTimer({
      hideTimer,
      setDeadline: setTimeoutDeadline
    })

    setBookingSession([])
    setFares([])
    setBookingCartData([])

    //Reset search conf
    setCount([])
  }, [
    credentials,
    reset,
    hideTimer,
    localLoading,
    setFolio,
    setTickets,
    setPayment,
    setConfirmation,
    setBookingDepartureTripId,
    setBookingReturningTripId,
    setBookingOrigin,
    setBookingDestination,
    setBookingDepartureDate,
    setBookingReturningDate,
    setBookingDepartureTrip,
    setBookingDepartureDisponibilidad,
    setBookingDepartureSelectedTickets,
    setBookingDepartureBlockedTickets,
    setBookingReturningTrip,
    setPassengers,
    setBookingGlobalTrips,
    setBookingReturningBlockedTickets,
    setSessionCanReserve,
    setBookingTickets,
    setBookingPayment,
    setBookingReset,
    setBookingSession,
    setFares,
    setBookingCartData,
    setCount,
  ])

  useEffect(() => {
    transpais.settings()
      .then(response => {
        setSettings(response.data)
      })
      .catch(error => {
        Sentry.captureException(error)
      })
  }, [setSettings])

  useEffect(() => {
    // Temporalmente deshabilitado: ensureElectronPinpadReady en Dashboard.
    // (Se mantiene el bloque comentado para reactivar cuando el flujo de auth esté estable.)
    let cancelled = false
    ;(async () => {
      if (!credentials?.name || !credentials?.value) return
      const result = await ensureElectronPinpadReady(credentials)
      if (cancelled) return
      if (!result.ok && !result.skipped && typeof window !== 'undefined' && window.electronAPI?.getPinpadStatus) {
        console.warn('[ELECTRON AUTH] Pinpad Electron no listo en Dashboard; se reintentó enviar credenciales', result.status)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [credentials?.name, credentials?.value])

  const readCode = (event) => {
    if (!isOnAdmin) {
      const key = event.key
      //loggerDebug('length', barcodeInputValue.length)
      setCodeKey(key)
    }
  }
  // useRay('dev dev')

  const setCodeKey = (key) => {
    if (key === 'Enter') {
      search()
      return
    }

    barcodeInputValue.current = barcodeInputValue.current + key

    if (barcodeInputValue.current.length > 12) {
      search()
    }
  }

  const predicate = (event) => (event.key >= '0' && event.key <= '9') || event.key === 'Enter'
  useKey(predicate, readCode, {event: 'keyup'})

  const search = () => {
    setLocalLoading(true)
    loggerDebug('search', barcodeInputValue)
    Sentry.setContext('Search', {
      folio: barcodeInputValue.current,
    })
    gateway
      .reservationSearch(token, barcodeInputValue.current, punto_venta_id, estacion_id)
      .then((response) => {
        setLocalLoading(false)
        loggerDebug(response.data)
        const info = response.data.header
        if (info.error) {
          //hideLoader()

          setupModal({
            title: 'Atención',
            content: <p>No es posible localizar esta reservación, valida tus datos e inténtalo de
              nuevo.</p>,
          })
          showModal()
          barcodeInputValue.current = ''
        } else {
          const data = response.data.content

          loggerDebug(info)
          loggerDebug(data)
          if (data.length > 0) {
            let empresa = data[0].itemDatosConexion
            const selectedData = data.map((ticket) => {
              ticket.selected = true
              return ticket
            })
            changeCompany(empresa)
            setTickets(selectedData)
            setFolio(barcodeInputValue.current)
            navigate(`${ BASE_URL }/reservations/review`)
          } else {
            barcodeInputValue.current = ''
          }
        }
      })
      .catch((error) => {
        loggerError(error)
        Sentry.captureException(error)
        setLocalLoading(false)
        setupModal({
          title: 'Atención',
          content: <p>No es posible localizar esta reservación, valida tus datos e inténtalo de
            nuevo.</p>,
        })
        showModal()
        barcodeInputValue.current = ''
      })
  }

  // DEBUG ONLY: delega compra de prueba a Electron (IPC).
  const startPinpadStepByStep = async () => {
    const flowId = startPinpadFlow({ trigger: 'dashboard-button' })
    const manualStartTime = Date.now()

    logFlowStep({
      phase: 'INIT',
      step: 'Button startPinpadStepByStep clicked',
      stepNumber: 1,
      status: 'START',
      data: { flowId },
    })

    try {
      const testReference = `TEST-${Math.random().toString(36).slice(2, 10).toUpperCase()}`
      const testAmount = 10000 // $100.00 MXN

      logFlowStep({
        phase: 'PURCHASE',
        step: 'Starting test purchase',
        stepNumber: 13,
        status: 'START',
        data: {
          reference: testReference,
          amount: testAmount,
          amountFormatted: `$${(testAmount / 100).toFixed(2)} MXN`,
        },
      })

      logFlowStep({
        phase: 'ELECTRON_SEND',
        step: 'ELECTRON_SEND: Sending test purchase to Electron',
        stepNumber: 14,
        status: 'START',
        data: { reference: testReference, amount: testAmount },
      })

      if (!window.electronAPI || typeof window.electronAPI.testPurchase !== 'function') {
        const missingApiError = new Error('electronAPI.testPurchase is not available (no preload bridge)')
        missingApiError.code = 'ELECTRON_API_NOT_AVAILABLE'
        throw missingApiError
      }

      logFlowStep({
        phase: 'ELECTRON_WAIT',
        step: 'ELECTRON_WAIT: Awaiting electronAPI.testPurchase (invoke/handle)',
        stepNumber: 15,
        status: 'START',
        data: { reference: testReference },
      })

      const electronResponse = await window.electronAPI.testPurchase({
        reference: testReference,
        amount: testAmount,
      })

      if (!electronResponse?.ok || !electronResponse?.charge) {
        const rpcError = new Error(electronResponse?.error || 'Electron testPurchase returned no charge')
        rpcError.code = electronResponse?.code || 'ELECTRON_TEST_PURCHASE_FAILED'
        throw rpcError
      }

      const purchaseResult = {
        success: true,
        charge: electronResponse.charge,
        voucherPrinted: electronResponse.voucherPrinted,
        voucherPrintError: electronResponse.voucherPrintError,
      }

      logFlowStep({
        phase: 'COMPLETE',
        step: 'Test purchase completed',
        stepNumber: 23,
        status: 'SUCCESS',
        duration: Date.now() - manualStartTime,
        data: {
          reference: testReference,
          foliocpagos: purchaseResult?.charge?.foliocpagos,
          auth: purchaseResult?.charge?.auth,
          amount: purchaseResult?.charge?.amount,
        },
      })

      setupModal({
        title: '✅ Compra Exitosa',
        content: (
          <>
            <p><strong>Referencia:</strong> {testReference}</p>
            <p><strong>Folio:</strong> {purchaseResult?.charge?.foliocpagos}</p>
            <p><strong>Autorización:</strong> {purchaseResult?.charge?.auth}</p>
            <p><strong>Monto:</strong> ${(testAmount / 100).toFixed(2)} MXN</p>
            <p><strong>Tarjeta:</strong> {purchaseResult?.charge?.cc_number}</p>
            <p><strong>Tipo:</strong> {purchaseResult?.charge?.cc_type}</p>
            <p><strong>Voucher cliente:</strong> {purchaseResult?.voucherPrinted ? 'Impreso ✅' : 'No impreso ⚠️'}</p>
            {!purchaseResult?.voucherPrinted && purchaseResult?.voucherPrintError ? (
              <p><strong>Detalle impresión:</strong> {purchaseResult?.voucherPrintError}</p>
            ) : null}
          </>
        ),
        actions: {
          positive: {
            title: 'Cerrar',
            onClick: () => {
              hideModal()
            },
          },
        },
      })
      showModal()
    } catch (error) {
      logFlowStep({
        phase: 'ERROR',
        step: 'Test flow failed',
        stepNumber: 24,
        status: 'ERROR',
        error: {
          code: error?.code || 'UNKNOWN',
          message: error?.message,
          stack: error?.stack,
          flowId: getPinpadFlowId(),
        },
      })
      loggerError('Error en test de PinPad', error)

      setupModal({
        title: '❌ Error en Compra',
        content: (
          <>
            <p><strong>Código:</strong> {error?.code || 'UNKNOWN'}</p>
            <p><strong>Mensaje:</strong> {error?.message || 'Error desconocido'}</p>
          </>
        ),
        actions: {
          positive: {
            title: 'Cerrar',
            onClick: () => {
              hideModal()
            },
          },
        },
      })
      showModal()
    }
  }

  return (
    <>
      <Loader show={ localLoading }/>
      {/*----Page----*/ }
      <section className={ [styles.sectionDashboard, 'section-full'].join(' ') }>
        <div className={ [styles.sectionDashboardContent, 'section-flex'].join(' ') }>
          <div className={ [styles.logo, 'logo'].join(' ') }>
            <figure className="logo-image logo-image-transpais-dark"/>
            <div className={styles.clock}>
              <Timer variant={ 'clock' } blur={true}/>
            </div>
          </div>
          <div className={ styles.modules }>
            <div className={ styles.modulesContent }>
              <Module label={ 'Compra tus boletos' } icon={ 'ticket' }
                      route={ `${ BASE_URL }/booking/search` } disabled={ false }/>
              {/* <Module label={ 'Paga tu reserva' } icon={ 'calendar-clock' }
                      route={ `${ BASE_URL }/reservations/lookup` }/> */}
              {/*<Module label={ 'Imprimir boletos' } icon={ 'print' }*/}
              {/*        route={ `${ BASE_URL }/reprints/search` }/>*/}
              {/* <Module label={ 'Reservar viaje' } icon={ 'ticket' }
                      route={ `${ BASE_URL }/reservationTrips/search` }/> */}
            </div>
            {/*<button type="button" onClick={startPinpadStepByStep}>test</button>*/}
          </div>
          <Footer/>
        </div>
        <div className={ styles.imageWrapper }>
          <figure className={ [styles.image, styles.imageDashboardBusBackground].join(' ') }/>
        </div>
      </section>
      {canToggleDevTools && (
        <DevToolsToggle
          type="button"
          aria-label="Abrir herramientas de desarrollo"
          onClick={ toggleDevTools }
        >
          dev
        </DevToolsToggle>
      )}
    </>
  )
}

export default Dashboard
